import { useState, useCallback, useRef, useEffect } from 'react';
import { GutenbergBlock, BlockEditorConfig } from '../types';
import { serializeBlocksToWordPress } from '../utils/blockSerializer';

export const useBlockManagement = (
  initialBlocks: GutenbergBlock[] = [],
  config?: BlockEditorConfig,
  initialTitle: string = ''
) => {
  const [blocks, setBlocks] = useState<GutenbergBlock[]>(initialBlocks);
  const [title, setTitle] = useState(initialTitle);
  const blocksRef = useRef<GutenbergBlock[]>([]);

  // Keep blocks ref in sync with blocks state
  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  const handleBlocksChange = useCallback((newBlocks: GutenbergBlock[]) => {
    setBlocks(newBlocks);
  }, []);

  const handleSave = useCallback(async () => {
    if (config?.onSave) {
      try {
        const serializedContent = serializeBlocksToWordPress(blocks);
        await config.onSave(serializedContent, blocks);
      } catch (error) {
        console.error('Error serializing blocks:', error);
      }
    }
  }, [blocks, config]);

  const addBlock = useCallback((blockType: string, attributes: Record<string, any> = {}, index?: number) => {
    const newBlock: GutenbergBlock = {
      name: blockType,
      attributes: {
        content: '',
        ...attributes
      },
      clientId: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      isValid: true,
      innerBlocks: []
    };

    if (typeof index === 'number') {
      setBlocks(prevBlocks => {
        const newBlocks = [...prevBlocks];
        newBlocks.splice(index, 0, newBlock);
        return newBlocks;
      });
    } else {
      setBlocks(prevBlocks => [...prevBlocks, newBlock]);
    }
  }, []);

  const updateBlock = useCallback((clientId: string, attributes: Record<string, any>) => {
    setBlocks(prevBlocks => 
      prevBlocks.map(block => 
        block.clientId === clientId 
          ? { ...block, attributes: { ...block.attributes, ...attributes } }
          : block
      )
    );
  }, []);

  const removeBlock = useCallback((clientId: string) => {
    setBlocks(prevBlocks => prevBlocks.filter(block => block.clientId !== clientId));
  }, []);

  const restoreBlocks = useCallback(() => {
    if (blocksRef.current.length > 0) {
      setBlocks(blocksRef.current);
      return true;
    }
    return false;
  }, []);

  return {
    blocks,
    setBlocks,
    title,
    setTitle,
    handleBlocksChange,
    handleSave,
    addBlock,
    updateBlock,
    removeBlock,
    restoreBlocks
  };
};
